<script setup lang="ts">
import { useHttp } from '@inertiajs/vue3';
import { Check, X, Star, Lock, Loader2 } from 'lucide-vue-next';
import { onMounted } from 'vue';
import { Button } from '@/components/ui/button';
import { useToast } from '@/composables/useToast';
import { plans } from '@/data/plans';
import * as billingRoutes from '@/routes/billing';

const { billing } = defineProps<{
    billing: {
        plan_code: string;
        plan_name: string;
        period_start: string | null;
        period_end: string | null;
        subscription_status:
            | 'active'
            | 'grace_period'
            | 'past_due'
            | 'canceled'
            | 'none';
        has_subscription: boolean;
        quotas: Record<
            string,
            { label: string; used: number; limit: number; remaining: number }
        >;
    };
}>();

const toast = useToast();
const portalHttp = useHttp<Record<string, never>, { url: string }>({});
const checkoutHttp = useHttp<Record<string, never>, { url: string }>({});

function openPortal() {
    portalHttp.post(billingRoutes.portal().url, {
        onSuccess: (data) => {
            window.location.href = data.url;
        },
        onError: () => {
            toast.error("Impossible d'accéder au portail de gestion.");
        },
    });
}

function openCheckout() {
    checkoutHttp.post(billingRoutes.checkoutSession().url, {
        onSuccess: (data) => {
            window.location.href = data.url;
        },
        onError: (errors) => {
            const message =
                typeof errors === 'object' &&
                errors !== null &&
                'message' in errors
                    ? String((errors as Record<string, unknown>).message)
                    : 'Impossible de démarrer la mise à niveau.';
            toast.error(message);
        },
    });
}

function formatDate(d: string | null) {
    if (!d) {
        return '—';
    }

    return new Date(d).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

onMounted(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('success') === '1') {
        toast.success('Abonnement Pro activé — bienvenue !');
        window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('canceled') === '1') {
        toast.info("Paiement annulé. Votre abonnement n'a pas été modifié.");
        window.history.replaceState({}, '', window.location.pathname);
    }
});
</script>

<template>
    <div class="space-y-8 p-6">
        <!-- Header -->
        <div>
            <h1 class="text-2xl font-semibold tracking-tight">Abonnement</h1>
            <p class="text-sm text-muted-foreground">
                Gérez votre plan et vos fonctionnalités
            </p>
        </div>

        <!-- Grace period banner -->
        <div
            v-if="billing.subscription_status === 'grace_period'"
            class="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800"
        >
            Votre abonnement Pro se termine le
            {{ formatDate(billing.period_end) }}. Vos données restent
            accessibles. Pour renouveler, utilisez le portail ci-dessous.
        </div>

        <!-- Past due banner -->
        <div
            v-else-if="billing.subscription_status === 'past_due'"
            class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
            Un paiement est en attente sur votre abonnement. Mettez à jour votre
            moyen de paiement via le portail pour éviter une interruption.
        </div>

        <!-- Plan cards -->
        <div class="grid max-w-3xl grid-cols-1 gap-8 md:grid-cols-2">
            <div
                v-for="plan in plans"
                :key="plan.code"
                :class="[
                    'group relative flex flex-col rounded-2xl border p-8 transition-colors',
                    billing.plan_code === plan.code
                        ? 'border-brand/40 bg-brand/[0.02]'
                        : 'border-border/40 hover:border-border',
                ]"
            >
                <!-- Current plan badge -->
                <div
                    v-if="billing.plan_code === plan.code"
                    class="absolute -top-3.5 left-6"
                >
                    <span
                        class="inline-flex items-center gap-1.5 rounded-full bg-brand px-3.5 py-1 text-xs font-medium text-white"
                    >
                        <Star class="h-3 w-3 fill-current" />
                        Plan actuel
                    </span>
                </div>

                <!-- Plan info -->
                <div class="mb-8">
                    <h3 class="mb-3 text-lg font-semibold text-foreground">
                        {{ plan.name }}
                    </h3>
                    <div class="mb-4 flex items-baseline gap-1">
                        <span
                            class="text-4xl font-semibold tracking-tight text-foreground"
                            >€{{ plan.price }}</span
                        >
                        <span class="text-sm text-muted-foreground">/mois</span>
                    </div>
                    <p class="text-sm leading-relaxed text-muted-foreground">
                        {{ plan.description }}
                    </p>
                </div>

                <!-- Features list -->
                <ul class="mb-10 flex-1 space-y-3.5">
                    <li
                        v-for="feature in plan.features"
                        :key="feature.text"
                        class="flex items-start gap-2.5"
                    >
                        <Check
                            v-if="feature.included"
                            class="mt-0.5 h-4 w-4 flex-shrink-0 text-brand"
                        />
                        <X
                            v-else
                            class="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground/40"
                        />
                        <span
                            :class="
                                feature.included
                                    ? 'text-sm text-muted-foreground'
                                    : 'text-sm text-muted-foreground/50 line-through'
                            "
                            >{{ feature.text }}</span
                        >
                    </li>
                </ul>

                <!-- CTA -->
                <template v-if="plan.code === 'free'">
                    <div
                        v-if="billing.plan_code === 'free'"
                        class="text-center text-sm text-muted-foreground"
                    >
                        Votre plan actuel
                    </div>
                    <div
                        v-else
                        class="text-center text-sm text-muted-foreground"
                    >
                        Votre précédent plan
                    </div>
                </template>

                <template v-else-if="plan.code === 'pro'">
                    <!-- Current Pro user -->
                    <div v-if="billing.has_subscription" class="space-y-3">
                        <p
                            v-if="billing.period_start && billing.period_end"
                            class="text-center text-xs text-muted-foreground"
                        >
                            Du {{ formatDate(billing.period_start) }} au
                            {{ formatDate(billing.period_end) }}
                        </p>
                        <Button
                            variant="outline"
                            size="lg"
                            class="w-full rounded-full font-medium"
                            :disabled="portalHttp.processing"
                            @click="openPortal"
                        >
                            <Loader2
                                v-if="portalHttp.processing"
                                class="mr-2 h-4 w-4 animate-spin"
                            />
                            Gérer l'abonnement
                        </Button>
                    </div>
                    <!-- Free user — upgrade CTA -->
                    <Button
                        v-else
                        size="lg"
                        class="w-full rounded-full bg-brand font-medium hover:bg-brand/85"
                        :disabled="checkoutHttp.processing"
                        @click="openCheckout"
                    >
                        <Loader2
                            v-if="checkoutHttp.processing"
                            class="mr-2 h-4 w-4 animate-spin"
                        />
                        Passer au plan Pro
                    </Button>
                </template>
            </div>
        </div>

        <!-- Quota section -->
        <div class="max-w-3xl space-y-4">
            <h2 class="text-base font-semibold text-foreground">Utilisation</h2>

            <!-- Free user: locked features -->
            <template v-if="!billing.has_subscription">
                <div
                    class="divide-y divide-border/40 rounded-xl border border-border/40"
                >
                    <div
                        v-for="(quota, key) in billing.quotas"
                        :key="key"
                        class="flex items-center justify-between px-5 py-3.5"
                    >
                        <div class="flex items-center gap-2.5">
                            <Lock
                                v-if="quota.limit === 0"
                                class="h-4 w-4 flex-shrink-0 text-muted-foreground/40"
                            />
                            <span class="text-sm text-foreground">{{
                                quota.label
                            }}</span>
                        </div>
                        <span class="text-xs text-muted-foreground">
                            <template v-if="quota.limit === 0"
                                >Non disponible</template
                            >
                            <template v-else-if="quota.limit === -1"
                                >Illimité</template
                            >
                            <template v-else
                                >{{ quota.used }} / {{ quota.limit }}</template
                            >
                        </span>
                    </div>
                </div>
                <p class="text-sm text-muted-foreground">
                    Les fonctionnalités IA nécessitent le
                    <button
                        class="font-medium text-brand underline-offset-2 hover:underline"
                        :disabled="checkoutHttp.processing"
                        @click="openCheckout"
                    >
                        plan Pro
                    </button>
                    .
                </p>
            </template>

            <!-- Pro user: all unlimited -->
            <template v-else>
                <div
                    class="divide-y divide-border/40 rounded-xl border border-border/40"
                >
                    <div
                        v-for="(quota, key) in billing.quotas"
                        :key="key"
                        class="flex items-center justify-between px-5 py-3.5"
                    >
                        <span class="text-sm text-foreground">{{
                            quota.label
                        }}</span>
                        <span class="text-xs text-muted-foreground">
                            <template v-if="quota.limit === -1">
                                <span
                                    class="flex items-center gap-1 text-brand"
                                >
                                    <Check class="h-3.5 w-3.5" />
                                    Illimité
                                </span>
                            </template>
                            <template v-else-if="quota.limit === 0"
                                >Non disponible</template
                            >
                            <template v-else
                                >{{ quota.used }} / {{ quota.limit }}</template
                            >
                        </span>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>
